import { Routes } from '@packages/enums';
import type { MenuProps } from 'antd';
import { Menu } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSidebarMenu } from './useSidebarMenu';
import Styles from '../Styles.module.scss';

interface SidebarMenuProps {
    collapsed: boolean;
}

// Helper function to recursively find the matching menu key for a pathname
const findMatchingKey = (items: MenuProps['items'], pathname: string): string | null => {
    if (!items) return null;

    for (const item of items) {
        if (!item) continue;

        // Check if current item matches
        if (item.key && typeof item.key === 'string') {
            // Handle exact match
            if (pathname === item.key) {
                return item.key;
            }
            // Handle routes with params (e.g., /data-hub/source-registry/:id)
            const keyPattern = item.key.replace(/:[^/]+/g, '[^/]+');
            const regex = new RegExp(`^${keyPattern}$`);
            if (regex.test(pathname)) {
                return item.key;
            }
            // Handle pathname starts with key (for parent routes)
            if (pathname.startsWith(item.key) && item.key !== '/') {
                return item.key;
            }
        }

        // Recursively check children (only SubMenuType has children)
        if ('children' in item && item.children) {
            const childMatch = findMatchingKey(item.children, pathname);
            if (childMatch) {
                return childMatch;
            }
        }
    }

    return null;
};

// Helper function to find the parent submenu key for a given menu key
const findParentSubmenuKey = (
    items: MenuProps['items'],
    targetKey: string,
    parentKey: string | null = null
): string | null => {
    if (!items) return null;

    for (const item of items) {
        if (!item) continue;

        const currentKey = item.key && typeof item.key === 'string' ? item.key : null;

        // Check if current item matches the target key
        if (currentKey === targetKey) {
            // If we found the target and have a parent key, return it
            return parentKey;
        }

        // Recursively check children (only SubMenuType has children)
        if ('children' in item && item.children) {
            // If current item is a submenu, pass its key as parent
            const submenuParentKey = currentKey && currentKey.endsWith('-submenu') ? currentKey : parentKey;
            const found = findParentSubmenuKey(item.children, targetKey, submenuParentKey);
            if (found !== null) {
                return found;
            }
        }
    }

    return null;
};

const SidebarMenu: React.FC<SidebarMenuProps> = ({ collapsed }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const menuItems = useSidebarMenu(collapsed);

    const selectedKey = useMemo(() => {
        const pathname = location.pathname;

        // Special case for chat history
        if (pathname === `${Routes.CHAT}/history`) {
            return pathname;
        }

        // Find matching key from menu items
        const matchingKey = findMatchingKey(menuItems, pathname);
        if (matchingKey) {
            return matchingKey;
        }

        // Fallback to root path
        const rootPath = '/' + pathname.split('/')[1];
        return rootPath;
    }, [location.pathname, menuItems]);

    const selectedKeys = useMemo(() => {
        const keys: string[] = [selectedKey];

        // Find parent submenu key if the selected item is inside a submenu
        const parentSubmenuKey = findParentSubmenuKey(menuItems, selectedKey);
        if (parentSubmenuKey) {
            keys.push(parentSubmenuKey);
        }

        return keys;
    }, [selectedKey, menuItems]);

    const [openKeys, setOpenKeys] = useState<string[]>(() => {
        const pathname = window.location.pathname;
        const matchingKey = findMatchingKey(menuItems, pathname);
        if (matchingKey) {
            const parentKey = findParentSubmenuKey(menuItems, matchingKey);
            return parentKey ? [parentKey] : [];
        }
        return [];
    });

    useEffect(() => {
        queueMicrotask(() => {
            if (collapsed) {
                // collapsed modda submenu açık olmasın
                setOpenKeys([]);
                return;
            }

            const matchingKey = findMatchingKey(menuItems, location.pathname);
            if (matchingKey) {
                const parentKey = findParentSubmenuKey(menuItems, matchingKey);
                if (parentKey) {
                    setOpenKeys(prev => {
                        if (!prev.includes(parentKey)) {
                            return [...prev, parentKey];
                        }
                        return prev;
                    });
                }
            }
        });
    }, [collapsed, location.pathname, menuItems]);

    const handleMenuClick: MenuProps['onClick'] = ({ key }: { key: string }) => {
        // Skip navigation for external links like documentation
        if (key === 'documentation') {
            return;
        }
        // If we're on a child route (e.g., /de-identification/:id) and clicking the parent route,
        // use replace to ensure proper navigation
        if (location.pathname.startsWith(key) && location.pathname !== key) {
            navigate(key, { replace: true });
        } else if (location.pathname !== key) {
            navigate(key);
        }
    };

    const handleOpenChange: MenuProps['onOpenChange'] = (keys: string[]) => {
        setOpenKeys(keys);
    };

    // Find parent submenu key to highlight it
    const parentSubmenuKey = useMemo(() => {
        return findParentSubmenuKey(menuItems, selectedKey);
    }, [menuItems, selectedKey]);

    return (
        <div className={classNames('flex-1 overflow-y-auto pt-5', collapsed ? 'px-2' : 'px-5')}>
            <Menu
                mode="inline"
                inlineCollapsed={collapsed}
                selectedKeys={selectedKeys}
                openKeys={openKeys}
                onOpenChange={handleOpenChange}
                items={menuItems}
                onClick={handleMenuClick}
                className={classNames('border-none font-medium', Styles.sidebarMenu)}
                data-selected-submenu={parentSubmenuKey || undefined}
            />
        </div>
    );
};

export default React.memo(SidebarMenu);
